import type { QueryClient, QueryKey } from '@tanstack/react-query';
import { useAuthStore } from '@/store/auth-store';
import { sessionRevision, sessionScope, sessionTenantId } from '@/features/auth/session-scope';

export const waEventUUID = (v: unknown): v is string => typeof v === 'string' && /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(v) && v !== '00000000-0000-0000-0000-000000000000';
export const waEventKeys = {
  feed: (scope = sessionScope(useAuthStore.getState())) => ['wa-events', scope, 'unacknowledged'] as const,
  channel: (id?: string, limit = 100, scope = sessionScope(useAuthStore.getState())) => ['wa-events', scope, 'channel', id, limit] as const,
  event: (id: string, scope = sessionScope(useAuthStore.getState())) => ['wa-events', scope, 'event', id] as const,
};
export function currentWAEventKey(key: QueryKey, scope = sessionScope(useAuthStore.getState()), channel?: string): boolean {
  return scope !== 'anonymous' && key[0] === 'wa-events' && key[1] === scope &&
    (key.length === 3 && key[2] === 'unacknowledged' || key.length === 5 && key[2] === 'channel' && (!channel || key[3] === channel) || key.length === 4 && key[2] === 'event');
}
export function ownedWAEventHint(value: unknown, envelopeTenant?: unknown): { id: string; channelId: string } | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const row = value as Record<string, unknown>, tenant = sessionTenantId(useAuthStore.getState());
  return waEventUUID(tenant) && row.tenant_id === tenant && (envelopeTenant === undefined || envelopeTenant === tenant) && waEventUUID(row.id) && waEventUUID(row.channel_id) ? { id: row.id, channelId: row.channel_id } : null;
}
/** Hints restart GETs for this owner only. Never fabricate a row or replay a POST. */
export async function reconcileWAEvents(client: QueryClient, channel?: string): Promise<void> {
  const revision = sessionRevision(), scope = sessionScope(useAuthStore.getState());
  if (scope === 'anonymous') return;
  const predicate = (query: { queryKey: QueryKey }) => currentWAEventKey(query.queryKey, scope, channel);
  await client.cancelQueries({ predicate });
  if (revision === sessionRevision()) await client.invalidateQueries({ predicate }, { cancelRefetch: false });
}
